/**
 * Command Center (Growth OS phase 2): KPIs, recommended next actions and data health over the Growth Graph.
 * Behind the server-evaluated `growth_os.command_center` flag (default off): off, unknown or failed -> the ordinary
 * not-found screen and no nav entry. A KPI the API did not measure (null) renders as "Not measured", never as 0.
 */
import { Link } from 'react-router';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, KitProvider, LoadingRegion, SkeletonText, type BadgeTone } from '@qm/ui/kit';
import { Heading, Loading, PageHeader } from '@qm/ui';
import { formatDate, formatNumber } from '@qm/i18n';
import { COMMAND_CENTER_FLAG, type CommandCenter, type Kpi, type Recommendation } from '@qm/contracts/command-center';
import { useApp } from '../../../lib/app-context';
import { useFlag } from '../../../lib/flags';
import { useResource } from '../../../lib/resource';
import { NotFoundScreen } from '../../../shell/RouteStates';
import { useWorkspaceName } from '../_shared/ListScreen';
import './command-center.css';

export function CommandCenterPage() {
  const { t } = useApp();
  const state = useFlag(COMMAND_CENTER_FLAG);
  if (state === 'off') return <NotFoundScreen />;
  if (state === 'loading') {
    return (
      <section className="qm-page" aria-labelledby="page-title">
        <Heading level={1} id="page-title">{t('growth-os.command.title')}</Heading>
        <Loading label={t('common.loading')} />
      </section>
    );
  }
  return <CommandCenterScreen />;
}

const PRIORITY_TONE: Record<Recommendation['priority'], BadgeTone> = { high: 'danger', medium: 'warning', low: 'neutral' };
const HEALTH_TONE: Record<CommandCenter['health']['level'], BadgeTone> = { good: 'success', attention: 'warning', unknown: 'neutral' };

function CommandCenterScreen() {
  const { t, locale } = useApp();
  const workspace = useWorkspaceName();
  const res = useResource<CommandCenter>('/v1/command-center');
  const fmtTime = (iso: string | null) => (iso ? formatDate(locale, new Date(iso), { dateStyle: 'medium', timeStyle: 'short' }) : t('growth-os.command.never'));
  return (
    <KitProvider locale={locale}>
      <section className="qm-page qc" aria-labelledby="page-title">
        <PageHeader eyebrow={workspace} title={t('growth-os.command.title')} intro={t('growth-os.command.intro')}
          actions={<Button variant="secondary" onClick={res.reload}>{t('growth-os.command.refresh')}</Button>} />
        {res.status === 'loading' ? (
          <LoadingRegion label={t('common.loading')}><SkeletonText lines={4} /></LoadingRegion>
        ) : res.status === 'error' ? (
          <EmptyState tone="error" title={t('shell.error.load')} actions={<Button onClick={res.reload}>{t('growth-os.command.retry')}</Button>} />
        ) : (
          <>
            <ul className="qc__kpis" aria-label={t('growth-os.command.kpis')}>
              {res.data.kpis.map((k) => <li key={k.id}><KpiCard kpi={k} /></li>)}
            </ul>
            <Card as="section" aria-labelledby="qc-recs">
              <CardHeader id="qc-recs" title={t('growth-os.command.recs.title')} description={t('growth-os.command.recs.intro')} />
              <CardBody>
                {res.data.recommendations.length === 0 ? (
                  <p className="qc__muted">{t('growth-os.command.recs.empty')}</p>
                ) : (
                  <ul className="qc__recs">
                    {res.data.recommendations.map((r) => (
                      <li key={r.id} className="qc__rec">
                        <Badge tone={PRIORITY_TONE[r.priority]}>{t(`growth-os.command.priority.${r.priority}`)}</Badge>
                        <span className="qc__rec-text">{t(r.titleKey, recParams(r, t))}</span>
                        {r.actionPath ? <Link className="qc__rec-link" to={r.actionPath}>{t('growth-os.command.recs.act')}</Link> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
            <Card as="section" aria-labelledby="qc-health">
              <CardHeader id="qc-health" title={t('growth-os.command.health.title')} actions={<Badge tone={HEALTH_TONE[res.data.health.level]} dot>{t(`growth-os.command.health.${res.data.health.level}`)}</Badge>} />
              <CardBody>
                <ul className="qc__sources">
                  {res.data.health.sources.map((s) => (
                    <li key={s.source} className="qc__source">
                      <span>{t(`growth-os.graph.source.${s.source}`)}</span>
                      <Badge tone={s.connected ? 'success' : 'neutral'}>{t(s.connected ? 'growth-os.command.health.connected' : 'growth-os.command.health.notConnected')}</Badge>
                      <span className="qc__muted">{t('growth-os.command.health.lastSync', { time: fmtTime(s.lastSyncAt) })}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </>
        )}
      </section>
    </KitProvider>
  );
}

/** Params for the title string; a connect_data `source` is shown by its translated name. */
function recParams(r: Recommendation, t: (k: string) => string): Record<string, string | number> {
  const p = { ...r.params };
  if (typeof p.source === 'string') p.source = t(`growth-os.graph.source.${p.source}`);
  return p;
}

export function KpiCard({ kpi }: { kpi: Kpi }) {
  const { t, locale } = useApp();
  const value = kpi.value;
  const measured = value !== null;
  const text = measured ? formatNumber(locale, value, kpi.unit === 'percent' ? { style: 'percent', maximumFractionDigits: 1 } : { maximumFractionDigits: 1 }) : t('growth-os.command.notMeasured');
  const delta = measured && kpi.previous !== null ? value - kpi.previous : null;
  return (
    <Card className="qc__kpi">
      <CardBody>
        <p className="qc__kpi-label">{t(kpi.labelKey)}</p>
        <p className="qc__kpi-value" data-measured={measured} dir="auto">{text}</p>
        {delta !== null && delta !== 0 ? <p className="qc__muted" data-trend={delta > 0 ? 'up' : 'down'}>{t(delta > 0 ? 'growth-os.command.up' : 'growth-os.command.down', { n: formatNumber(locale, Math.abs(delta), { maximumFractionDigits: 1 }) })}</p> : null}
        {kpi.measuredAt ? <p className="qc__muted">{t('growth-os.command.asOf', { time: formatDate(locale, new Date(kpi.measuredAt), { dateStyle: 'medium' }) })}</p> : null}
      </CardBody>
    </Card>
  );
}
